import { useChat } from "@ai-sdk/react";
import {
  type AttachmentList,
  CITATIONS_PART_TYPE,
  type ConversationDetail,
  CSRF_HEADER,
  type FolderId,
  familyIdOf,
  isModelKey,
  MEMORY_PROPOSAL_PART,
  MEMORY_PROPOSAL_PART_ID,
  type MemoryProposalView,
  QUESTION_ANSWER_PART,
  REVISION_REQUEST_PART,
  TASK_PROPOSAL_PART,
  type TaskProposalView,
  type TurnOptions,
  taskProposalPartId,
  WIKI_CHANGES_PART,
  WIKI_SUGGESTION_PART,
  WIKI_SUGGESTION_PART_ID,
  type WikiChangesCard as WikiChangesCardData,
  type WikiSuggestionView,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookPlus, FileDown, Flag } from "lucide-react";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api, blockedBySessionEnd } from "../api.ts";
import { ok } from "../api-errors.ts";
import { ReportFailedTurn, useConversationFeedback } from "../feedback/ConversationFeedback.tsx";
import { FeedbackDraftCard } from "../feedback/FeedbackDraftCard.tsx";
import { MemoryProposalCard, type MemoryProposalDecision } from "../memory/MemoryProposalCard.tsx";
import { EffortPicker, type PickerState, useEffortChoice } from "../models/EffortPicker.tsx";
import { useModelOptions } from "../models/queries.ts";
import { canChange } from "../projects/capabilities.ts";
import { FolderField } from "../projects/FolderPicker.tsx";
import { type KnowledgeSettings, useProjects } from "../projects/queries.ts";
import { changeSetPath, Link, wikiChangesPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { holdReload } from "../shell/build-refresh.ts";
import { useUnsavedWork } from "../shell/session-ended.ts";
import { TaskProposalCard } from "../tasks/TaskProposalCard.tsx";
import { AudienceLine, WebResultsNote } from "../trust/AudienceLine.tsx";
import { peopleAudience } from "../trust/audience.ts";
import { NoKnowledgeNote } from "../trust/NoKnowledgeNote.tsx";
import { ResidencyNote } from "../trust/ResidencyNote.tsx";
import { useQueuedInstructions } from "../wiki/instructions.ts";
import { wikiKeys } from "../wiki/queries.ts";
import { ResolutionRunCard } from "../wiki/ResolutionRunCard.tsx";
import { RevisionRequestCard } from "../wiki/RevisionRequestCard.tsx";
import {
  queueResolve,
  RESOLUTION_REQUEST_PART,
  type ResolutionRequestData,
  resolutionOf,
  useQueuedResolve,
} from "../wiki/resolve.ts";
import { threadKeys, useRevisionRequests } from "../wiki/threads.ts";
import { liveCardIndex, WikiChangesCard, WikiDraftUpdatedLine } from "../wiki/WikiChangesCard.tsx";
import { WikiSuggestionCard, type WikiSuggestionDecision } from "../wiki/WikiSuggestionCard.tsx";
import {
  AttachmentPicker,
  AttachmentReview,
  AttachmentTray,
  useAttachmentContext,
} from "./attachments/AttachmentControls.tsx";
import { AttachmentWorkspace } from "./attachments/AttachmentWorkspace.tsx";
import {
  type AttachmentDraft,
  canSendAttachments,
  REVIEW_PAUSE_MS,
} from "./attachments/controller.ts";
import { freshAttachmentList, useAttachmentDraft } from "./attachments/queries.ts";
import { useAttachmentStart } from "./attachments/start.ts";
import { ComposerField, ComposerFooter, ComposerFrame } from "./Composer.tsx";
import { createChat } from "./chat.ts";
import { useChatCache } from "./chat-cache.tsx";
import { ranCode } from "./code-run.ts";
import { composerFocusPending, takeComposerFocus } from "./composer-focus.ts";
import { authorizedHistory, reconcileAuthorizedHistory } from "./history.ts";
import { type ChatMessage, MessageView } from "./MessageView.tsx";
import { questionAnswersOf } from "./QuestionCard.tsx";
import { conversationQuery } from "./queries.ts";
import { SaveDialog, saveRefusal, TargetPicker } from "./SaveDialog.tsx";
import { SuggestedTitleField, useSuggestedTitle } from "./SuggestedTitle.tsx";
import { Timeline } from "./Timeline.tsx";
import { type DecisionFailure, decisionFailureOf, turnRefusalOf } from "./tool-approvals.ts";
import { includesWebResults } from "./web-results.ts";

/** The reader follows the reply while within this distance of the bottom. */
const FOLLOW_SLACK = 160;
/** A finished answer lands this far below the top, so the question's last line stays in view. */
const ANSWER_OFFSET = 24;

/**
 * Where the thread scrolls after a change, or null to leave the reader where they are. On
 * opening and while the reader follows: the bottom, unless the latest answer is finished
 * (`answerTop`, its offset in the scroller), which lands at `min(bottom, answerTop - 24px)`. A
 * new question always goes to the bottom; a reader who scrolled up is never moved.
 */
export function followTarget(at: {
  opened: boolean;
  asked: boolean;
  following: boolean;
  answerTop: number | null;
  scrollHeight: number;
  clientHeight: number;
}): number | null {
  if (!at.opened && !at.asked && !at.following) {
    return null;
  }
  const bottom = Math.max(0, at.scrollHeight - at.clientHeight);
  if (at.asked || at.answerTop === null) {
    return bottom;
  }
  return Math.max(0, Math.min(bottom, at.answerTop - ANSWER_OFFSET));
}

/** The composer of a conversation that does not exist yet: sending creates it. */
export function StartConversation(props: {
  projectId: string;
  residency: "eu" | "global" | undefined;
  /** Sent once on mount (a question asked from home); `onAsked` then drops it. */
  initialQuestion: string | null;
  /** The effort picked on home for that question. */
  initialOptions?: TurnOptions | undefined;
  onAsked: () => void;
  /** The conversation exists and its first message is on its way (its chat is in the cache). */
  onStarted: (id: string) => void;
  onTurnEnd: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  const cache = useChatCache();
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const choice = useEffortChoice(useModelOptions({ projectId: props.projectId }).data);
  const { draft } = useAttachmentDraft(`new:project:${props.projectId}`);
  const attachments = useAttachmentStart({
    source: draft,
    csrfToken,
    options: choice.turnOptions,
    create: () =>
      api.api.projects[":projectId"].conversations
        .$post({ param: { projectId: props.projectId } }, { headers: { [CSRF_HEADER]: csrfToken } })
        .then(ok),
    started: (created) => props.onStarted(created.id),
  });
  const start = async (text: string, handedOff?: TurnOptions) => {
    if (starting || attachments.starting || draft.snapshot().files.length > 0) {
      return false;
    }
    setStarting(true);
    // The question is in flight until the chat holds it, even if this screen is left meanwhile.
    const release = holdReload();
    setError(null);
    try {
      const created = await ok(
        await api.api.projects[":projectId"].conversations.$post(
          { param: { projectId: props.projectId } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      );
      // Sent from this handler, not from an effect: exactly once, whatever React replays.
      const chat = createChat(created.id, csrfToken, [], () => props.onTurnEnd(created.id));
      const options = handedOff ?? choice.turnOptions();
      void chat.sendMessage({ text }, options ? { body: { options } } : undefined);
      cache.keep(created.id, chat, true);
      props.onStarted(created.id);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : t("errors.unexpected"));
      // Keep what was typed.
      return false;
    } finally {
      // After the route change's own attempt: by then the kept chat streams and holds.
      setTimeout(release, 0);
      setStarting(false);
    }
  };
  // The handed-over question is asked once, even if StrictMode replays the effect.
  const asked = useRef(false);
  const [restored, setRestored] = useState<{ text: string; cause: unknown } | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on mount
  useEffect(() => {
    if (props.initialQuestion && !asked.current) {
      asked.current = true;
      const question = props.initialQuestion;
      props.onAsked();
      void start(question, props.initialOptions).then((sent) => {
        if (!sent) {
          setRestored({ text: question, cause: {} });
        }
      });
    }
  }, []);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 items-end justify-center overflow-y-auto px-4 pb-4">
        <p className="max-w-md pb-2 text-center text-sm text-fg-secondary">
          {t("conversation.empty")}
        </p>
      </div>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-2 px-3 pb-4 sm:px-4">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {attachments.error && draft.snapshot().files.length > 0 ? (
          <Notice tone="danger">
            {attachments.error}
            <Button variant="ghost" onClick={() => attachments.select([])}>
              {t("attachments.retry")}
            </Button>
          </Notice>
        ) : null}
        <Composer
          draft={draft}
          onFiles={starting ? () => {} : attachments.select}
          starting={attachments.starting}
          disabled={starting || attachments.starting}
          onSend={(text) => start(text)}
          restore={restored}
          picker={
            choice.view ? (
              <EffortPicker
                view={choice.view}
                onPick={choice.pick}
                disabled={starting || attachments.starting}
              />
            ) : null
          }
        />
        <ResidencyNote value={props.residency} className="self-center" />
      </div>
    </div>
  );
}

export interface ThreadProps {
  /** Null for a standalone conversation (no knowledge, saves pick a project). */
  projectId: string | null;
  conversationId: string;
  /** Why nothing can be sent here, or null when the caller can continue. */
  readOnly: string | null;
  /** Save an answer as a project file (`file.save_answer`). */
  canSaveFile: boolean;
  /** Save an answer to the wiki as a draft (`wiki.draft`). */
  canSaveWiki: boolean;
  canOpen: (documentId: string) => boolean;
  settings: KnowledgeSettings | undefined;
  audience: { text: string; restricted: boolean } | null;
  residency: "eu" | "global" | undefined;
  onTurnEnd: () => void;
  /** Says whether any answer of the thread used web results, whenever that changes. */
  onWebResults?: (included: boolean) => void;
  /** Says whether code ran for any answer of the thread (agent-turn D20), when that changes. */
  onRanCode?: (ran: boolean) => void;
}

/**
 * The thread of an opened conversation: the chat kept for it (streaming, or just started), else
 * one built from the history the caller may see now (`initialMessages`).
 */
export function Thread(props: ThreadProps & { initialMessages: ChatMessage[] }) {
  return (
    <AttachmentWorkspace conversationId={props.conversationId}>
      <LiveThread key={props.conversationId} {...props} />
    </AttachmentWorkspace>
  );
}

/**
 * The history of a conversation as the chat reads it (hidden messages keep their slot). A
 * pending memory proposal joins its message as the part the stream would have written.
 */
export function chatMessagesOf(detail: ConversationDetail): ChatMessage[] {
  const proposals = new Map((detail.memoryProposals ?? []).map((p) => [p.messageId, p]));
  const suggestions = new Map((detail.wikiSuggestions ?? []).map((w) => [w.messageId, w]));
  const taskProposals = detail.taskProposals ?? [];
  return detail.messages.map((m) => {
    const proposal = proposals.get(m.id as never);
    const suggestion = suggestions.get(m.id as never);
    return {
      id: m.id,
      role: m.role,
      parts: [
        ...(m.parts as ChatMessage["parts"]),
        ...(proposal
          ? [{ type: MEMORY_PROPOSAL_PART, id: MEMORY_PROPOSAL_PART_ID, data: proposal } as const]
          : []),
        ...(suggestion
          ? [{ type: WIKI_SUGGESTION_PART, id: WIKI_SUGGESTION_PART_ID, data: suggestion } as const]
          : []),
        // The creator's task proposals under their answer (agent-turn D11), any state.
        ...taskProposals
          .filter((p) => p.messageId === m.id)
          .map(
            (p) => ({ type: TASK_PROPOSAL_PART, id: taskProposalPartId(p.id), data: p }) as const,
          ),
      ] as ChatMessage["parts"],
      metadata: { hidden: m.hidden, createdAt: m.createdAt },
    };
  });
}

/** The Wiki changes card a turn streamed and stored (wiki-collaboration D8), if any. */
function wikiChangesOf(message: ChatMessage): WikiChangesCardData | null {
  const part = message.parts.find((p) => p.type === WIKI_CHANGES_PART) as
    | { data?: WikiChangesCardData }
    | undefined;
  return part?.data ?? null;
}

/** The wiki suggestion streamed with (or reloaded under) an answer, if any. */
function suggestionOf(
  message: ChatMessage,
): (WikiSuggestionView & { decided?: WikiSuggestionDecision }) | null {
  const part = message.parts.find((p) => p.type === WIKI_SUGGESTION_PART) as
    | { data?: WikiSuggestionView & { decided?: WikiSuggestionDecision } }
    | undefined;
  return part?.data ?? null;
}

/** The task proposals streamed with (or reloaded under) an answer (agent-turn D11). */
function taskProposalsOf(message: ChatMessage): TaskProposalView[] {
  return message.parts.flatMap((p) =>
    p.type === TASK_PROPOSAL_PART ? [(p as { data: TaskProposalView }).data] : [],
  );
}

/** Records a task proposal's decision on its part of the chat (client-only, like `settlePart`). */
function settleTaskProposal(messages: ChatMessage[], view: TaskProposalView): ChatMessage[] {
  return messages.map((m) =>
    m.id !== view.messageId
      ? m
      : {
          ...m,
          parts: m.parts.map((p) =>
            p.type === TASK_PROPOSAL_PART && (p as { data: TaskProposalView }).data.id === view.id
              ? { ...p, data: view }
              : p,
          ) as ChatMessage["parts"],
        },
  );
}

/** The memory proposal streamed with (or reloaded under) an answer, if any. */
function proposalOf(
  message: ChatMessage,
): (MemoryProposalView & { decided?: MemoryProposalDecision }) | null {
  const part = message.parts.find((p) => p.type === MEMORY_PROPOSAL_PART) as
    | { data?: MemoryProposalView & { decided?: MemoryProposalDecision } }
    | undefined;
  return part?.data ?? null;
}

/**
 * Records a card's decision on its part of the chat (client-only, never sent: the history sent
 * back drops these parts). A chat kept across route changes then shows the card settled.
 */
export function settlePart(
  messages: ChatMessage[],
  messageId: string,
  type: typeof WIKI_SUGGESTION_PART | typeof MEMORY_PROPOSAL_PART,
  decided: WikiSuggestionDecision | MemoryProposalDecision,
): ChatMessage[] {
  return messages.map((m) =>
    m.id !== messageId
      ? m
      : {
          ...m,
          parts: m.parts.map((p) =>
            p.type === type ? { ...p, data: { ...(p as { data: object }).data, decided } } : p,
          ) as ChatMessage["parts"],
        },
  );
}

function LiveThread(props: ThreadProps & { initialMessages: ChatMessage[] }) {
  const { projectId, conversationId, readOnly, canSaveFile, canSaveWiki, canOpen } = props;
  const { settings, audience } = props;
  const { t } = useTranslation();
  const artefactScope = useMemo(() => ({ projectId, conversationId }), [projectId, conversationId]);
  const { csrfToken } = useSession();
  const client = useQueryClient();
  const attachments = { data: useAttachmentContext()?.list };
  const { draft, state: draftState } = useAttachmentDraft(conversationId);
  const cache = useChatCache();
  const onTurnEnd = useRef(props.onTurnEnd);
  onTurnEnd.current = props.onTurnEnd;
  const [chat] = useState(
    () =>
      cache.take(conversationId) ??
      createChat(conversationId, csrfToken, props.initialMessages, () => onTurnEnd.current()),
  );
  useEffect(() => {
    cache.keep(conversationId, chat);
    // Leaving (another route, the sidebar): kept only while a reply still streams into it.
    return () => cache.leave(conversationId, chat);
  }, [cache, conversationId, chat]);
  const { messages, sendMessage, status, error } = useChat<ChatMessage>({ chat });
  const streaming = status === "submitted" || status === "streaming";
  // Revision requested cards (wiki-collaboration D15): read for the creator, refreshed when a
  // turn ends (the agent's write settles them).
  const revisions = useRevisionRequests(projectId, conversationId, !readOnly);
  // Only when a turn ends: the query already loads on mount.
  const wasStreaming = useRef(streaming);
  useEffect(() => {
    if (wasStreaming.current && !streaming && projectId) {
      void client.invalidateQueries({ queryKey: threadKeys.revisions(projectId, conversationId) });
      // A resolution run settles when its turn ends: the set's eligibility and agent revision.
      void client.invalidateQueries({ queryKey: [...wikiKeys.all(projectId), "change"] });
    }
    wasStreaming.current = streaming;
  }, [streaming, client, projectId, conversationId]);
  // Render authorization changes immediately, while preserving local cards/streamed answers
  // when a benign detail-cache update has not changed the persisted message itself.
  const historyVersion = useRef(authorizedHistory.get(chat) ?? props.initialMessages);
  // The detail shown when the check after a send applied a newer one (`reconcileSend`): never
  // merged again. Merged against the newer baseline, it would drop every newer message for a
  // render, remounting the answers (an open citation preview closed, #355).
  const shown = useRef(props.initialMessages);
  useEffect(() => {
    // Committed only: a render React discards never counts as shown.
    shown.current = props.initialMessages;
  }, [props.initialMessages]);
  const passed = useRef<ChatMessage[] | null>(null);
  const superseded = props.initialMessages === passed.current;
  const thread = superseded
    ? messages
    : reconcileAuthorizedHistory(
        messages,
        historyVersion.current,
        props.initialMessages,
        streaming,
      );
  useEffect(() => {
    if (superseded) {
      return;
    }
    passed.current = null;
    historyVersion.current = props.initialMessages;
    authorizedHistory.set(chat, props.initialMessages);
    if (thread !== messages) {
      chat.messages = thread;
    }
  }, [props.initialMessages, thread, messages, chat, superseded]);
  const choice = useEffortChoice(
    useModelOptions(readOnly ? null : { conversationId }, { enabled: !streaming }).data,
  );
  const handedEffort = useRef(false);
  useEffect(() => {
    if (!handedEffort.current && draftState.options) {
      handedEffort.current = true;
      // Kept in the draft until a send carries it: leaving first keeps the pick (#207 R2).
      choice.pick({
        effort: draftState.options.effort ?? null,
        // A draft of an older SPA holds an exact key: picked as its family (D9).
        family: draftFamily(draftState.options.family ?? draftState.options.model),
        labProviders: draftState.options.labProviders,
        ...(draftState.options.reasoning !== undefined && {
          reasoning:
            draftState.options.reasoning === "preset" ? null : draftState.options.reasoning,
        }),
      });
    }
  }, [draftState.options, choice.pick]);
  const firstSend = thread.length === 0;
  const pickEffort = useCallback(
    (state: PickerState) => {
      choice.pick(state);
      // Before the first send the pick lives in the draft too; afterwards the server keeps it.
      if (firstSend || draft.snapshot().options) {
        draft.edit({
          options: {
            ...(state.effort !== null && { effort: state.effort }),
            ...(state.family !== null && { family: state.family }),
            ...(state.labProviders &&
              Object.keys(state.labProviders).length > 0 && { labProviders: state.labProviders }),
            ...(state.reasoning !== undefined && { reasoning: state.reasoning ?? "preset" }),
          },
        });
      }
    },
    [choice.pick, draft, firstSend],
  );
  // A refused decision (expired, answered already) is told on its row, not as a failed turn.
  const refused = decisionFailureOf(error);
  const turnError = refused ? undefined : error;
  // A turn refused at its start by a spend limit or by attachments being off says why (never a figure).
  const turnRefusal = turnRefusalOf(turnError);
  const [toolFailures, setToolFailures] = useState<ReadonlyMap<string, DecisionFailure>>(
    () => new Map(),
  );
  useEffect(() => {
    const failed = decisionFailureOf(error);
    if (failed) {
      setToolFailures((current) => new Map(current).set(failed.approvalId, failed.failure));
    }
  }, [error]);
  const decide = (approvalId: string, approved: boolean) => {
    void chat.addToolApprovalResponse({ id: approvalId, approved });
  };

  const busy = status === "submitted" || status === "streaming";
  // Feedback drafts and "Report a problem" (feedback-from-conversation): not in a read-only view.
  const feedback = useConversationFeedback(conversationId, !readOnly, busy, messages);
  useUnsavedWork(busy);
  const scroller = useRef<HTMLElement>(null);
  // Grows with every streamed character, so the effect follows the reply as it is written.
  const written = thread.reduce(
    (n, m) => n + m.parts.reduce((k, p) => k + (p.type === "text" ? p.text.length : 1), 0),
    0,
  );
  // Whether the reader follows the reply: near the bottom when they last scrolled (growth alone
  // never fires a scroll, so a tall block landing does not read as the reader scrolling up).
  const following = useRef(true);
  const followed = useRef(false);
  // How many questions the thread holds: one more means a question was just asked (counted, as
  // the stored history swaps the local message ID for the server's after the turn).
  const lastQuestion = useRef(0);
  useEffect(() => {
    const el = scroller.current;
    if (!el) {
      return;
    }
    const onScroll = () => {
      following.current = el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_SLACK;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  const latest = thread.at(-1);
  // A finished answer (or one whose citations are in) lands with its start in view.
  const landing =
    latest?.role === "assistant" &&
    (!busy || latest.parts.some((p) => p.type === CITATIONS_PART_TYPE))
      ? latest.id
      : null;
  const question = thread.filter((m) => m.role === "user").length;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `written` and `busy` are the triggers
  useEffect(() => {
    // The thread's own scroll only, never the page. Opens on the latest answer, follows the reply
    // while the reader is near the bottom (not while they read further up), and once it
    // finishes stops with the answer's start in view. A new question always follows.
    const el = scroller.current;
    if (!el) {
      return;
    }
    const asked = followed.current && question > lastQuestion.current;
    lastQuestion.current = question;
    const answer = landing
      ? el.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(landing)}"]`)
      : null;
    const target = followTarget({
      opened: !followed.current,
      asked,
      following: following.current,
      answerTop: answer
        ? answer.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop
        : null,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
    });
    followed.current = true;
    if (target !== null) {
      el.scrollTop = target;
      following.current = el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_SLACK;
    }
  }, [written, busy, landing]);
  // What was last sent: put back in the composer if its turn fails.
  const lastSent = useRef("");
  // The question the last pick answered, until a turn succeeds: a resend of the same text after
  // a failure answers it again (agent-turn D7).
  const pendingAnswer = useRef<{ toolCallId: string; text: string } | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const reconcileSend = async () => {
    setChecking(true);
    setCheckFailed(false);
    try {
      // Both observations must succeed before retry. Never infer a commit from local chat state.
      const [detail, list] = await Promise.all([
        client.fetchQuery(conversationQuery(conversationId)),
        freshAttachmentList(client, conversationId),
      ]);
      if (!list.composer) {
        throw new Error("metadata");
      }
      // Through the authorized-history merge, like every refresh of the detail: hidden messages
      // still go, while local parts the server no longer lists (a memory confirmation) stay.
      // Only persisted messages remain: a question refused before it committed leaves the thread
      // (its text returns to the composer below).
      const current = chatMessagesOf(detail);
      const stored = new Set(current.map((m) => m.id));
      chat.messages = reconcileAuthorizedHistory(
        chat.messages,
        historyVersion.current,
        current,
        false,
      ).filter((m) => stored.has(m.id));
      historyVersion.current = current;
      authorizedHistory.set(chat, current);
      // Until the view renders this detail (it is in the query cache now), the one it shows is
      // older than the baseline: not merged again. One already showing this history is not.
      passed.current =
        JSON.stringify(shown.current) === JSON.stringify(current) ? null : shown.current;
      draft.reconcile(list);
      const pendingText = draft.snapshot().pendingText;
      draft.edit({
        checkingSend: false,
        pendingText: undefined,
        checkBeforeSend: undefined,
        ...((chat.error ||
          list.composer.attachmentStates.some(
            (s) => draft.snapshot().selected.includes(s.attachmentId) && !s.sent,
          )) &&
        pendingText
          ? { text: draft.snapshot().text || pendingText }
          : {}),
      });
    } catch {
      const pendingText = draft.snapshot().pendingText;
      if (pendingText && !draft.snapshot().text) {
        draft.edit({ text: pendingText });
      }
      setCheckFailed(true);
    } finally {
      setChecking(false);
    }
  };
  const send = async (text: string) => {
    const state = draft.snapshot();
    if (state.checkingSend) {
      return false;
    }
    const ids = [...state.selected];
    let authorized: AttachmentList | undefined;
    if (ids.length) {
      // Snapshot is synchronous: a second submit cannot enter while access is checked.
      draft.edit({ checkingSend: true, pendingText: text, checkBeforeSend: undefined });
      setCheckFailed(false);
      setChecking(true);
      // Defend against a second tab consuming IDs after this tab's last poll.
      try {
        const list = await freshAttachmentList(client, conversationId);
        if (!canSendAttachments(list, ids)) {
          draft.reconcile(list);
          // Read and refused before sending: nothing to check; the question returns to the
          // composer, whose hint names the reason (#207 L1).
          draft.edit({ checkingSend: false, pendingText: undefined });
          return false;
        }
        authorized = list;
      } catch {
        // Unread before sending: held for Retry check, said as never sent, even after a remount.
        draft.edit({ checkBeforeSend: true });
        setCheckFailed(true);
        return false;
      } finally {
        setChecking(false);
      }
    }
    lastSent.current = text;
    // The restored choice of a failed pick, sent again: still the answer to its question.
    const answering =
      pendingAnswer.current && pendingAnswer.current.text === text.trim()
        ? pendingAnswer.current
        : null;
    pendingAnswer.current = answering;
    const options = choice.turnOptions();
    const refs =
      authorized?.attachments
        .filter((a) => ids.includes(a.id))
        .map((a) => ({
          documentId: a.id,
          title: a.title,
          mediaType: a.mediaType,
        })) ?? [];
    await sendMessage(
      ids.length
        ? {
            parts: [
              { type: "text", text },
              { type: "data-attachments", data: { attachments: refs } },
            ],
          }
        : { text },
      options || ids.length || answering
        ? {
            body: {
              ...(options ? { options } : {}),
              ...(ids.length ? { attachments: ids } : {}),
              ...(answering ? { questionAnswer: { toolCallId: answering.toolCallId } } : {}),
            },
          }
        : undefined,
    );
    if (chat.status !== "error") {
      pendingAnswer.current = null;
    }
    // Sent with the pick: the server keeps it from now on, so the draft lets it go. Not when
    // the options had not loaded (nothing carried it) or the turn failed (#207 R2).
    if (options && chat.status !== "error" && draft.snapshot().options) {
      draft.edit({ options: undefined });
    }
    // The SDK can report stream errors through state rather than rejecting sendMessage.
    if (ids.length) {
      await reconcileSend();
    }
  };
  // Instructions to the agent, composed in the wiki preview's tray (wiki-collaboration-ux D3):
  // the author's own plain message, sent as this conversation's next turn once it is free.
  // Read through a ref: `choice` and the draft are new objects each render, and a callback that
  // changed every render would re-run the hand-off's effect (and take a second batch early).
  const instructionDeps = useRef({ choice, sendMessage, chat, draft });
  instructionDeps.current = { choice, sendMessage, chat, draft };
  const sendInstructions = useCallback((text: string) => {
    const { choice, sendMessage, chat, draft } = instructionDeps.current;
    // A failed turn restores this message for Retry, as a typed one would be.
    lastSent.current = text;
    const options = choice.turnOptions();
    void sendMessage({ text }, options ? { body: { options } } : undefined).then(() => {
      if (options && chat.status !== "error" && draft.snapshot().options) {
        draft.edit({ options: undefined });
      }
    });
  }, []);
  // Clarifying questions (agent-turn D7, D8): a pick is the next user message, bound by the
  // server to its question; the optimistic part marks the card answered at once.
  const pickChoice = (toolCallId: string, text: string, choiceIndex: number) => {
    lastSent.current = text;
    pendingAnswer.current = { toolCallId, text };
    const options = choice.turnOptions();
    void sendMessage(
      {
        parts: [
          { type: "text", text },
          { type: QUESTION_ANSWER_PART, data: { toolCallId, choiceIndex } },
        ],
      },
      { body: { questionAnswer: { toolCallId }, ...(options ? { options } : {}) } },
    ).then(() => {
      if (chat.status !== "error") {
        pendingAnswer.current = null;
      }
      if (options && chat.status !== "error" && draft.snapshot().options) {
        draft.edit({ options: undefined });
      }
    });
  };
  // "Answer in your own words": the composer takes focus, nothing prefilled.
  const [composerFocus, setComposerFocus] = useState(0);
  useQueuedInstructions(
    projectId && !readOnly ? conversationId : undefined,
    !busy,
    sendInstructions,
  );
  // Resolve with the agent (wiki-resolve-with-agent D2), from the set's card or its preview: the
  // server composes the turn; the optimistic message carries only the run card's marker.
  const resolveDeps = useRef({ choice, sendMessage, projectId, t });
  resolveDeps.current = { choice, sendMessage, projectId, t };
  const sendResolve = useCallback((request: ResolutionRequestData) => {
    const { choice, sendMessage, projectId, t } = resolveDeps.current;
    if (!projectId) {
      return;
    }
    const options = choice.turnOptions();
    void sendMessage(
      {
        parts: [
          {
            type: "text",
            text: t("wiki.resolve.sent", { count: request.threadCount, title: request.title }),
          },
          { type: RESOLUTION_REQUEST_PART, data: request },
        ],
      },
      {
        body: {
          resolution: { projectId, changeSetId: request.changeSetId },
          ...(options ? { options } : {}),
        },
      },
    );
  }, []);
  useQueuedResolve(projectId && !readOnly ? conversationId : undefined, !busy, sendResolve);
  const [announced, setAnnounced] = useState(false);
  useEffect(() => {
    if (busy) {
      setAnnounced(true);
    }
  }, [busy]);
  // The answer being saved as a file, with the title typed in "Save to wiki", if any.
  const [saving, setSaving] = useState<{ id: string; title: string } | null>(null);
  const savingMessage = saving ? thread.find((m) => m.id === saving.id) : undefined;
  const webResults = thread.some(
    (m) => m.role === "assistant" && !m.metadata?.hidden && includesWebResults(m.parts),
  );
  const onWebResults = useRef(props.onWebResults);
  onWebResults.current = props.onWebResults;
  useEffect(() => {
    onWebResults.current?.(webResults);
  }, [webResults]);
  const anyCode = thread.some(
    (m) => m.role === "assistant" && !m.metadata?.hidden && ranCode(m.parts),
  );
  const onRanCode = useRef(props.onRanCode);
  onRanCode.current = props.onRanCode;
  useEffect(() => {
    onRanCode.current?.(anyCode);
  }, [anyCode]);
  // The message index under which each change set's live card sits (its latest writer).
  const latestCard = liveCardIndex(thread.map(wikiChangesOf));
  // A failed send leaves its optimistic answer in the chat: the card opens again (the server
  // stored nothing, or ignores a second answer), and resending the restored choice keeps its
  // question (`pendingAnswer`).
  const lastUserIndex = thread.findLastIndex((m) => m.role === "user");
  // The message whose question is still asked: the last one, or the one before a failed send.
  const questionIndex =
    error && lastUserIndex === thread.length - 1 ? lastUserIndex - 1 : thread.length - 1;
  const questionAnswers = questionAnswersOf(
    error ? thread.filter((_, i) => i !== lastUserIndex) : thread,
  );
  const last = thread.at(-1);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <section
        data-esi-content="messages"
        ref={scroller}
        aria-label={t("conversation.thread")}
        aria-busy={busy}
        // Wide blocks in answers may widen past the reading column (#379).
        className="wide-host min-h-0 flex-1 overflow-y-auto"
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6">
          {/* Answers' own headings (Sources, Web results, Markdown) start at h3. */}
          <h2 className="sr-only">{t("conversation.thread")}</h2>
          {thread.length === 0 && !busy ? (
            <p className="text-sm text-fg-secondary">
              {projectId === null ? t("attachments.emptyConversation") : t("conversation.empty")}
            </p>
          ) : null}
          {thread.map((m, i) => {
            // The last message is persisted only once its turn ends: no passage read before.
            const streaming = busy && i === thread.length - 1 && m.role === "assistant";
            const savable = m.role === "assistant" && !m.metadata?.hidden && !streaming;
            const proposal = savable && !readOnly ? proposalOf(m) : null;
            const suggestion = savable && !readOnly ? suggestionOf(m) : null;
            const feedbackDraft = savable ? feedback.draftFor(m) : null;
            // The run card sits under its turn's answer, or under the question until one streams.
            const run =
              m.role === "assistant"
                ? resolutionOf(thread[i - 1])
                : thread[i + 1]?.role === "assistant"
                  ? null
                  : resolutionOf(m);
            return (
              <Fragment key={m.id}>
                <MessageView
                  message={m}
                  projectId={projectId ?? ""}
                  conversationId={busy && i === thread.length - 1 ? undefined : conversationId}
                  canOpen={canOpen}
                  streaming={streaming}
                  audience={m.role === "assistant" ? audience : null}
                  onToolDecision={readOnly ? undefined : decide}
                  toolFailures={toolFailures}
                  busy={busy}
                  superseded={i < thread.length - 1}
                  artefacts={artefactScope}
                  questions={{
                    answers: questionAnswers,
                    mine: !readOnly,
                    sending: checking || draftState.checkingSend === true,
                    open: i === questionIndex,
                    ...(!readOnly && {
                      onPick: pickChoice,
                      onOwnWords: () => setComposerFocus((n) => n + 1),
                    }),
                  }}
                  cards={
                    <>
                      {projectId && !readOnly
                        ? taskProposalsOf(m).map((p) => (
                            <TaskProposalCard
                              key={p.id}
                              projectId={projectId}
                              conversationId={conversationId}
                              proposal={p}
                              onSettled={(view) => {
                                chat.messages = settleTaskProposal(chat.messages, view);
                              }}
                            />
                          ))
                        : null}
                      {proposal ? (
                        <MemoryProposalCard
                          key={proposal.id}
                          proposal={proposal}
                          decided={proposal.decided}
                          askWhy={!busy}
                          onDecided={(decided) => {
                            chat.messages = settlePart(
                              chat.messages,
                              m.id,
                              MEMORY_PROPOSAL_PART,
                              decided,
                            );
                          }}
                        />
                      ) : null}
                    </>
                  }
                  actions={
                    savable && (canSaveFile || canSaveWiki || feedback.enabled) ? (
                      <div className="flex items-center gap-0.5">
                        {feedback.enabled ? (
                          <IconAction
                            label={t("feedbackDraft.reportProblem")}
                            disabled={feedback.pending}
                            onClick={() => feedback.start({ messageId: m.id })}
                          >
                            <Flag aria-hidden="true" />
                          </IconAction>
                        ) : null}
                        {/* "Save to wiki" is the save action; a file is its dialog's secondary path. */}
                        {canSaveFile && !canSaveWiki ? (
                          <IconAction
                            label={t("conversation.saveAsFile")}
                            onClick={() => setSaving({ id: m.id, title: "" })}
                          >
                            <FileDown aria-hidden="true" />
                          </IconAction>
                        ) : null}
                        {canSaveWiki ? (
                          <SaveToWiki
                            projectId={projectId}
                            conversationId={conversationId}
                            messageId={m.id}
                            webResults={includesWebResults(m.parts)}
                            {...(canSaveFile && {
                              onSaveAsFile: (title: string) => setSaving({ id: m.id, title }),
                            })}
                          />
                        ) : null}
                      </div>
                    ) : null
                  }
                />
                {projectId && wikiChangesOf(m) ? (
                  // One live card per change set, under the latest turn that wrote into it;
                  // earlier turns keep one line (wiki-collaboration-ux D8).
                  latestCard.get((wikiChangesOf(m) as WikiChangesCardData).changeSetId) === i ? (
                    <WikiChangesCard
                      projectId={projectId}
                      card={wikiChangesOf(m) as WikiChangesCardData}
                      canDraft={canSaveWiki === true && !readOnly}
                      busy={busy}
                      {...(!readOnly && {
                        onResolve: (request: ResolutionRequestData) =>
                          queueResolve(conversationId, request),
                      })}
                    />
                  ) : (
                    <WikiDraftUpdatedLine
                      changeSetId={(wikiChangesOf(m) as WikiChangesCardData).changeSetId}
                    />
                  )
                ) : null}
                {suggestion ? (
                  <WikiSuggestionCard
                    key={suggestion.id}
                    decided={suggestion.decided}
                    onDecided={(decided) => {
                      chat.messages = settlePart(
                        chat.messages,
                        m.id,
                        WIKI_SUGGESTION_PART,
                        decided,
                      );
                    }}
                    conversationId={conversationId}
                    suggestion={suggestion}
                    askWhy={!busy}
                  />
                ) : null}
                {feedbackDraft ? (
                  // After the answer's other cards (feedback-from-conversation D3).
                  <FeedbackDraftCard key={feedbackDraft.id} draft={feedbackDraft} askWhy={!busy} />
                ) : null}
                {projectId && run ? (
                  <ResolutionRunCard
                    projectId={projectId}
                    changeSetId={run.changeSetId}
                    runId={run.runId}
                    revision={run.revision}
                    threadCount={run.threadCount}
                    parts={m.role === "assistant" ? m.parts : []}
                    streaming={busy && i === thread.length - 1}
                    canAct={!readOnly}
                  />
                ) : null}
              </Fragment>
            );
          })}
          {projectId && !readOnly
            ? (revisions.data ?? []).map((request) => (
                <RevisionRequestCard
                  key={request.threadId}
                  request={request}
                  canRevise={!busy}
                  onRevise={(r) => {
                    const options = choice.turnOptions();
                    void sendMessage(
                      {
                        parts: [
                          { type: "text", text: r.comment },
                          {
                            type: REVISION_REQUEST_PART,
                            data: {
                              threadId: r.threadId,
                              changeSetId: r.changeSetId,
                              itemId: r.itemId,
                              itemTitle: r.itemTitle,
                              requestedByName: r.requestedByName,
                              ...(r.quote && { quote: r.quote }),
                            },
                          },
                        ],
                      },
                      {
                        body: {
                          revision: {
                            projectId,
                            changeSetId: r.changeSetId,
                            threadId: r.threadId,
                          },
                          ...(options ? { options } : {}),
                        },
                      },
                    );
                  }}
                />
              ))
            : null}
          {/* The answer's own timeline shows its live steps (agent-turn D4); until its first
              step arrives, one live line says the turn started. */}
          {busy && last?.role !== "assistant" ? <Timeline steps={[]} live /> : null}
        </div>
      </section>
      <p role="status" className="sr-only">
        {status === "submitted"
          ? t("conversation.status.submitted")
          : status === "streaming"
            ? t("conversation.status.streaming")
            : announced && !turnError
              ? t("conversation.status.done")
              : ""}
      </p>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-2 px-3 pb-4 sm:px-4">
        {turnError ? (
          <Notice
            tone="danger"
            {...(feedback.enabled &&
              !turnRefusal && {
                action: (
                  <ReportFailedTurn
                    pending={feedback.pending}
                    onReport={(from) =>
                      feedback.start(
                        {
                          failed: true,
                          // The failed turn's answer, when one was stored (its tools and errors).
                          ...(thread.at(-1)?.role === "assistant" && {
                            messageId: thread.at(-1)?.id,
                          }),
                        },
                        from,
                      )
                    }
                  />
                ),
              })}
          >
            {t(
              turnRefusal === "user_budget"
                ? "conversation.userShareRefused"
                : turnRefusal === "model_budget"
                  ? "conversation.budgetRefused"
                  : turnRefusal === "attachments_disabled"
                    ? "attachments.attachments_disabled"
                    : "conversation.turnFailed",
            )}
          </Notice>
        ) : null}
        {feedback.failed ? <Notice tone="danger">{t("feedbackDraft.reportFailed")}</Notice> : null}
        {feedback.dialog}
        {checking ? <Notice>{t("attachments.checkingSend")}</Notice> : null}
        {checkFailed || (draftState.checkingSend && !busy && !checking) ? (
          <Notice
            action={
              <Button
                variant="outline"
                size="sm"
                disabled={checking}
                onClick={() => void reconcileSend()}
              >
                {t("attachments.retryCheck")}
              </Button>
            }
          >
            {t(
              draftState.checkBeforeSend
                ? "attachments.checkFailedBefore"
                : "attachments.checkFailed",
            )}
          </Notice>
        ) : null}
        {readOnly ? (
          <Notice>{readOnly}</Notice>
        ) : (
          // After a failed turn (`error`), sending again is allowed.
          <Composer
            draft={draft}
            list={attachments.data}
            onFiles={(files) => draft.add(files)}
            // A failed turn's message comes back unless something new is being typed.
            restore={
              turnError
                ? { text: lastSent.current || lastUserText(thread), cause: turnError }
                : null
            }
            disabled={busy || checking || draftState.checkingSend}
            onSend={send}
            focusRequest={composerFocus}
            placeholder={projectId === null ? t("standalone.ask") : undefined}
            picker={choice.view ? <EffortPicker view={choice.view} onPick={pickEffort} /> : null}
          />
        )}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {projectId === null ? <NoKnowledgeNote /> : null}
          <ResidencyNote value={props.residency} />
        </div>
      </div>
      {saving ? (
        <SaveDialog
          open
          onClose={() => setSaving(null)}
          projectId={projectId}
          conversationId={conversationId}
          messageId={saving.id}
          initialTitle={saving.title}
          settings={settings}
          webResults={savingMessage ? includesWebResults(savingMessage.parts) : false}
          ranCode={savingMessage ? ranCode(savingMessage.parts) : false}
        />
      ) : null}
    </div>
  );
}

/** The latest user message's text: the first turn is sent at creation, outside `send`. */
function lastUserText(thread: readonly ChatMessage[]): string {
  const last = [...thread].reverse().find((m) => m.role === "user");
  return (last?.parts ?? []).flatMap((p) => (p.type === "text" ? [p.text] : [])).join("\n");
}

/**
 * "Save to wiki": a draft in the review queue; nothing is published. The dialog asks for the
 * draft's title (pre-filled with a suggestion), and for the project from a standalone
 * conversation; an answer that used web results says so before saving.
 */
export function SaveToWiki(props: {
  /** Null from a standalone conversation: the user picks the project first. */
  projectId: string | null;
  conversationId: string;
  messageId: string;
  webResults: boolean;
  /** "Save as a file instead" (`file.save_answer`): opens the file dialog with this title. */
  onSaveAsFile?: (title: string) => void;
}) {
  const { webResults, conversationId, messageId, onSaveAsFile } = props;
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const standalone = props.projectId === null;
  const [target, setTarget] = useState<string | null>(props.projectId);
  // A Wiki folder of the target for the draft (its root unless picked); a new target resets it.
  const [folder, setFolder] = useState<{ projectId: string | null; id: FolderId | null }>({
    projectId: null,
    id: null,
  });
  const folderId = folder.projectId === target ? folder.id : null;
  const projects = useProjects();
  const canOrganise = projects.data?.some((p) => p.id === target && canChange(p, "folder.manage"));
  const title = useSuggestedTitle({
    conversationId,
    messageId,
    sink: "wiki",
    target: standalone ? target : null,
    // Only once the dialog is open: the suggestion is a metered call.
    enabled: confirming && target !== null,
  });
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.api.conversations[":conversationId"].messages[":messageId"].wiki.$post(
          {
            param: { conversationId, messageId },
            json: {
              ...(title.value.trim() ? { title: title.value.trim() } : {}),
              ...(standalone && target ? { targetProjectId: target } : {}),
              ...(folderId ? { folderId } : {}),
            },
          },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => {
      setConfirming(false);
      return target ? queryClient.invalidateQueries({ queryKey: wikiKeys.all(target) }) : undefined;
    },
  });
  if (save.isSuccess) {
    return (
      <span className="px-2 text-xs text-fg-secondary" aria-live="polite">
        {t("wiki.savedToWiki")}{" "}
        <Link
          href={
            // The previous API sends no change set: its review queue lists the proposal.
            save.data.changeSetId
              ? changeSetPath(target ?? "", save.data.changeSetId, save.data.id)
              : wikiChangesPath(target ?? "")
          }
          className="font-medium text-accent-text underline-offset-4 hover:underline"
        >
          {t("wiki.reviewDraft")}
        </Link>
        {/* Saved to the wiki, the answer may still be saved as a file (review fix, Codex). */}
        {onSaveAsFile ? (
          <IconAction label={t("conversation.saveAsFile")} onClick={() => onSaveAsFile("")}>
            <FileDown aria-hidden="true" />
          </IconAction>
        ) : null}
      </span>
    );
  }
  return (
    <>
      <IconAction
        label={t("wiki.saveToWiki")}
        disabled={save.isPending}
        onClick={() => setConfirming(true)}
      >
        <BookPlus aria-hidden="true" />
      </IconAction>
      <AppDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={webResults ? t("wiki.saveWeb.title") : t("wiki.saveToWiki")}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!save.isPending && target !== null) {
              save.mutate();
            }
          }}
        >
          {standalone ? (
            <TargetPicker capability="wiki.draft">
              {(picked) => <PickedTarget value={picked} onChange={setTarget} />}
            </TargetPicker>
          ) : null}
          <SuggestedTitleField
            label={t("wiki.draftTitle")}
            placeholder={t("wiki.draftTitlePlaceholder")}
            title={title}
          />
          {target ? (
            <FolderField
              projectId={target}
              section="wiki"
              value={folderId}
              onChange={(id) => setFolder({ projectId: target, id })}
              canCreate={canOrganise}
            />
          ) : null}
          <WikiAudience
            conversationId={conversationId}
            messageId={messageId}
            target={standalone ? target : null}
            enabled={confirming && target !== null}
            webResults={webResults}
          />
          {webResults ? (
            <div className="flex flex-col gap-1.5">
              <WebResultsNote className="text-sm font-medium text-fg" />
              <p className="text-sm text-fg-secondary">{t("wiki.saveWeb.body")}</p>
            </div>
          ) : null}
          {save.isError ? <Notice tone="danger">{saveRefusal(save.error, t)}</Notice> : null}
          <DialogFooter sticky>
            {onSaveAsFile ? (
              <Button
                type="button"
                variant="ghost"
                className="sm:mr-auto"
                onClick={() => {
                  setConfirming(false);
                  onSaveAsFile(title.value);
                }}
              >
                <FileDown aria-hidden="true" />
                {t("wiki.saveAsFileInstead")}
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={() => setConfirming(false)}>
              {t("wiki.saveWeb.cancel")}
            </Button>
            <Button type="submit" disabled={save.isPending || target === null}>
              {webResults ? t("wiki.saveWeb.confirm") : t("wiki.saveToWiki")}
            </Button>
          </DialogFooter>
        </form>
      </AppDialog>
    </>
  );
}

/**
 * What "Save to wiki" does and who could read it (wiki-suggestions, design §2): a draft in the
 * review queue, read by the target's people who can read the answer's sources. The audience is
 * the `sink=wiki` save preview; when it fails (an older API, the network) only the first line
 * shows, and saving is never blocked.
 */
function WikiAudience(props: {
  conversationId: string;
  messageId: string;
  /** The picked project of a standalone conversation; null in a project conversation. */
  target: string | null;
  enabled: boolean;
  webResults: boolean;
}) {
  const { conversationId, messageId, target } = props;
  const { t } = useTranslation();
  const { userId } = useSession();
  const preview = useQuery({
    queryKey: ["conversation-save", conversationId, messageId, target, "wiki"],
    enabled: props.enabled,
    gcTime: 0,
    retry: false,
    queryFn: async () =>
      ok(
        await api.api.conversations[":conversationId"].messages[":messageId"].save.$get({
          param: { conversationId, messageId },
          query: { sink: "wiki", ...(target ? { targetProjectId: target } : {}) },
        }),
      ),
  });
  const readers = preview.data?.readers ?? [];
  return (
    <section aria-label={t("audience.label")} className="flex flex-col gap-1.5">
      <p className="text-sm text-fg-secondary">{t("wiki.saveReviewNote")}</p>
      {preview.data ? (
        <AudienceLine
          restricted
          text={peopleAudience(
            readers.length,
            readers.length === 1 && readers[0]?.userId === userId,
          )}
          webResults={props.webResults}
        />
      ) : null}
    </section>
  );
}

/** A quiet icon button under an answer: its name is its tooltip and accessible label. */
function IconAction(props: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={props.label}
          disabled={props.disabled}
          onClick={props.onClick}
          className="text-fg-muted hover:text-fg"
        >
          {props.children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{props.label}</TooltipContent>
    </Tooltip>
  );
}

/** `onSend` resolving false keeps the draft. Selected files never silently become text-only. */
function Composer(props: {
  placeholder?: string;
  disabled: boolean;
  onSend: (text: string) => unknown;
  restore?: { text: string; cause: unknown } | null;
  picker?: ReactNode;
  draft: AttachmentDraft;
  list?: AttachmentList | undefined;
  onFiles: (files: File[]) => void;
  /** A conversation is being created for the picked files (Home, a new project conversation). */
  starting?: boolean;
  /** Takes focus each time this number grows ("Answer in your own words", agent-turn D8). */
  focusRequest?: number;
}) {
  const { t } = useTranslation();
  const state = useSyncExternalStore(props.draft.subscribe, props.draft.snapshot);
  const { draft } = props;
  const paperclip = useRef<HTMLButtonElement>(null);
  const reviewButton = useRef<HTMLButtonElement>(null);
  // The number of new files the review opened for by itself; none when opened explicitly.
  const [lead, setLead] = useState<number | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  // Opened by Work on this with ESI: the composer takes focus once, after the route settles.
  useEffect(() => {
    if (composerFocusPending(draft.conversationId)) {
      const timer = setTimeout(() => {
        if (takeComposerFocus(draft.conversationId)) {
          field.current?.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [draft.conversationId]);
  useEffect(() => {
    if ((props.focusRequest ?? 0) > 0) {
      field.current?.focus();
    }
  }, [props.focusRequest]);
  // The control that opened the review dialog gets focus back: the paperclip or Manage.
  const reviewOpener = useRef<HTMLElement | null>(null);
  useUnsavedWork(
    state.text.trim() !== "" || state.selected.length > 0 || state.files.length > 0 || state.review,
    state.text,
  );
  const restore = props.restore;
  // biome-ignore lint/correctness/useExhaustiveDependencies: one restoration per failure
  useEffect(() => {
    if (restore?.text && !draft.snapshot().text) {
      draft.edit({ text: restore.text });
    }
  }, [restore?.cause]);
  const existing = !draft.conversationId.startsWith("new:");
  const openReview = (opener: HTMLElement | null) => {
    reviewOpener.current = opener;
    setLead(null);
    draft.openReview();
  };
  // The files land first; their review opens once, after a pause, when the composer metadata
  // is there (attachments-polish D1). A timer, not motion: reduced motion keeps it.
  const metadataLoaded = props.list?.composer !== undefined;
  // A standalone conversation: files upload private at once, with no review (standalone-
  // attachments-private D3). Known only once the metadata says so: until then they wait.
  const privateOnly = props.list?.composer?.privateOnly === true;
  const staged = state.files.some((f) => f.phase === "review");
  useEffect(() => {
    if (existing && privateOnly && staged && props.list?.composer?.canUpload) {
      draft.attach(props.list);
    }
  }, [existing, privateOnly, staged, props.list, draft]);
  // Never while the question is being written: typing since the files were staged holds the
  // review until focus leaves the field (or the text is back as it was), then the pause restarts.
  const [fieldFocused, setFieldFocused] = useState(false);
  useEffect(() => {
    const el = field.current;
    if (!el) {
      return;
    }
    const focus = () => setFieldFocused(true);
    const blur = () => setFieldFocused(false);
    setFieldFocused(document.activeElement === el);
    el.addEventListener("focus", focus);
    el.addEventListener("blur", blur);
    return () => {
      el.removeEventListener("focus", focus);
      el.removeEventListener("blur", blur);
    };
  }, []);
  const typing = fieldFocused && state.text !== (state.reviewText ?? "");
  useEffect(() => {
    if (
      !existing ||
      privateOnly ||
      state.reviewDue === 0 ||
      state.review ||
      !metadataLoaded ||
      typing
    ) {
      return;
    }
    const timer = setTimeout(() => {
      const now = draft.snapshot();
      const due = now.reviewDue;
      const writing =
        document.activeElement === field.current && now.text !== (now.reviewText ?? "");
      if (due > 0 && !writing) {
        reviewOpener.current = reviewButton.current;
        setLead(due);
        draft.openReview();
      }
    }, REVIEW_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [existing, privateOnly, state.reviewDue, state.review, metadataLoaded, typing, draft]);
  // The review is due only where there is one: never in a standalone conversation, and not
  // before the metadata says which (the files read Preparing meanwhile).
  const toReview = existing && metadataLoaded && !privateOnly && staged;
  const localPending = state.files.some(
    (f) =>
      !f.attachmentId ||
      draft.hasAttempt(f.key) ||
      ["uploading", "queued", "checking"].includes(f.phase),
  );
  const selected = state.selected.length > 0 || state.files.some((file) => !file.retained);
  const valid = !localPending && canSendAttachments(props.list, state.selected);
  const policy = props.list?.composer?.reason;
  const reason =
    !existing && state.files.length > 0
      ? // The failure guidance only once a creation failed; in flight, a quiet status.
        props.starting
        ? t("attachments.opening")
        : t("attachments.creationPending")
      : toReview
        ? t("attachments.reviewHint")
        : selected
          ? !state.text.trim()
            ? t("attachments.question")
            : !props.list?.composer && existing
              ? t("attachments.metadata")
              : !valid && policy
                ? `${t(policy === "unavailable" ? "attachments.reasonUnavailable" : `attachments.${policy}`)} ${t("attachments.removeToSend")}`
                : !valid
                  ? t("attachments.blocked")
                  : null
          : null;
  const submit = () => {
    if (!state.text.trim() || props.disabled || !valid || blockedBySessionEnd()) {
      return;
    }
    const text = state.text;
    draft.edit({ text: "" });
    void Promise.resolve(props.onSend(text))
      .then((sent) => {
        if (sent === false && !draft.snapshot().text) {
          draft.edit({ text });
        }
      })
      .catch(() => {
        if (!draft.snapshot().text) {
          draft.edit({ text });
        }
      });
  };
  return (
    <>
      <ComposerFrame
        onFiles={props.disabled ? undefined : props.onFiles}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <AttachmentTray
          list={props.list}
          draft={draft}
          state={state}
          onManage={existing ? openReview : undefined}
        />
        <ComposerField
          value={state.text}
          onChange={(text) => draft.edit({ text })}
          onSend={submit}
          placeholder={props.placeholder ?? t("conversation.ask")}
          inputRef={field}
        />
        <ComposerFooter
          disabled={props.disabled || !valid || !state.text.trim()}
          picker={props.picker}
          leading={
            <AttachmentPicker
              buttonRef={paperclip}
              onFiles={props.disabled ? () => {} : props.onFiles}
              disabled={props.disabled}
              // A standalone conversation picks files directly: there is nothing to review.
              onOpen={existing && !privateOnly ? () => openReview(paperclip.current) : undefined}
            />
          }
        />
        {toReview ? (
          <div className="flex min-w-0 items-center gap-2 px-5 pb-3">
            <p className="min-w-0 flex-1 text-xs text-fg-secondary">{reason}</p>
            <Button
              ref={reviewButton}
              type="button"
              variant="ghost"
              size="xs"
              onClick={(e) => openReview(e.currentTarget)}
            >
              {t("attachments.review")}
            </Button>
          </div>
        ) : reason ? (
          <p className="px-5 pb-3 text-xs text-fg-secondary">{reason}</p>
        ) : null}
        {/* Said once per batch: the line stays the same while files wait for their review. */}
        <p aria-live="polite" className="sr-only">
          {toReview ? t("attachments.reviewHint") : ""}
        </p>
      </ComposerFrame>
      {existing ? (
        <AttachmentReview
          conversationId={draft.conversationId}
          list={props.list}
          draft={draft}
          state={state}
          lead={lead}
          returnFocus={() =>
            reviewOpener.current?.isConnected
              ? reviewOpener.current
              : reviewButton.current?.isConnected
                ? reviewButton.current
                : paperclip.current
          }
        />
      ) : null}
    </>
  );
}

/** Reports the picked project up (the wiki draft is created in it). */
function PickedTarget(props: { value: string; onChange: (value: string) => void }) {
  const { value, onChange } = props;
  useEffect(() => {
    onChange(value);
  }, [value, onChange]);
  return null;
}

/** A draft's picked family: a family ID, or an exact key read as its family. */
function draftFamily(value: string | undefined): string | null {
  if (!value) {
    return null;
  }
  return isModelKey(value) ? (familyIdOf(value) ?? value) : value;
}
